import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { DocumentIllustration, type IllustrationFileType } from '../DocumentIllustration';
import { FileTypePill } from './FileTypePill';
import { useContentThumbnail } from '../../../hooks/useContentThumbnail';

export interface ContentThumbnailBannerProps {
  content: {
    id?: string;
    thumbnailUrl?: string;
    thumbnail_url?: string;
    mimeType?: string;
    mime_type?: string;
    title?: string;
    fileName?: string;
    file_name?: string;
    name?: string;
  };
  /** Pre-resolved file type override. */
  fileType?: IllustrationFileType;
  /** Banner height at rest, in px. Default 130. */
  height?: number;
  /** Banner height on enclosing card hover, in px. Default 188. If equal to `height`, no expansion. */
  hoverHeight?: number;
  /** Illustration size when no thumbnail. */
  illustrationSize?: 'normal' | 'compact';
  /** Overlay slots */
  bookmark?: React.ReactNode;
  bannerExtras?: React.ReactNode;
  /** Render the file-type pill in the top-left. Default true. */
  showFileTypePill?: boolean;
  sx?: SxProps<Theme>;
}

const ARCHIVE_MIMES = new Set([
  'application/zip',
  'application/x-zip-compressed',
  'application/x-tar',
  'application/gzip',
  'application/x-gzip',
]);

const resolveType = (content: ContentThumbnailBannerProps['content']): IllustrationFileType => {
  const mimeType = (content.mimeType ?? content.mime_type ?? '').toLowerCase();
  const name = (content.fileName ?? content.file_name ?? content.name ?? '').toLowerCase();
  if (mimeType === 'application/vnd.sharp.view') return 'view';
  if (ARCHIVE_MIMES.has(mimeType) || /\.(zip|tar|tar\.gz|tgz)$/.test(name)) return 'archive';
  if (mimeType.startsWith('audio/') || /\.(wav|mp3|flac|aac|ogg|m4a)$/.test(name)) return 'audio';
  if (mimeType.startsWith('video/') || /\.(mp4|mov|webm|avi|mkv)$/.test(name)) return 'video';
  if (mimeType.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/.test(name)) return 'img';
  if (mimeType === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint') || /\.pptx?$/.test(name)) return 'ppt';
  if (mimeType === 'text/html' || /\.html?$/.test(name)) return 'html';
  return 'doc';
};

/**
 * Full banner area used in card-style tiles: thumbnail image (or DocumentIllustration
 * fallback) over a file-type-tinted background, plus overlay slots for the file-type
 * pill (top-left), bookmark (top-right), and any banner extras.
 *
 * The banner expands from `height` to `hoverHeight` when the closest `.MuiCard-root`
 * ancestor is hovered. This delegates the hover behavior to the wrapping Card.
 */
export const ContentThumbnailBanner: React.FC<ContentThumbnailBannerProps> = ({
  content,
  fileType,
  height = 130,
  hoverHeight = 188,
  illustrationSize = 'normal',
  bookmark,
  bannerExtras,
  showFileTypePill = true,
  sx,
}) => {
  const resolvedType = fileType ?? resolveType(content);
  const hint = content.thumbnailUrl ?? content.thumbnail_url;
  const url = useContentThumbnail(content.id, hint);
  const expand = hoverHeight !== height;
  return (
    <Box
      sx={[
        (theme) => ({
          position: 'relative',
          height,
          overflow: 'hidden',
          backgroundColor: url ? undefined : theme.palette.fileTints[resolvedType],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: expand ? 'height 0.28s cubic-bezier(.2,.8,.2,1)' : undefined,
          ...(expand && {
            '.MuiCard-root:hover &': { height: hoverHeight },
          }),
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {url ? (
        <Box
          component="img"
          src={url}
          alt={content.title ?? content.name ?? content.fileName ?? content.file_name ?? ''}
          loading="lazy"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'top',
          }}
        />
      ) : (
        <DocumentIllustration fileType={resolvedType} size={illustrationSize} />
      )}

      {showFileTypePill && (
        <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 2 }}>
          <FileTypePill fileType={resolvedType} />
        </Box>
      )}

      {/* Bookmark slot sits at right: 46 to leave right: 10 for the wrapping
          Card's kebab button (rendered by ContentTile). */}
      {bookmark && (
        <Box sx={{ position: 'absolute', top: 10, right: 46, zIndex: 2 }}>
          {bookmark}
        </Box>
      )}

      {/* Caller-provided extras slot — sits further left of the bookmark. */}
      {bannerExtras && (
        <Box sx={{ position: 'absolute', top: 10, right: 82, zIndex: 2 }}>
          {bannerExtras}
        </Box>
      )}
    </Box>
  );
};

export default ContentThumbnailBanner;
